import React, { useState, useEffect, useMemo, useRef } from 'react';
import Papa from 'papaparse';
import { 
  Upload, 
  Database, 
  AlertCircle, 
  CheckCircle, 
  Trash2, 
  Search, 
  Plus, 
  Loader2, 
  ChevronDown, 
  ChevronRight, 
  FileSpreadsheet, 
  X, 
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { db } from '../../firebase';
import { collection, writeBatch, doc, getDocs, addDoc, deleteDoc } from 'firebase/firestore';

interface CareerItem {
  id: string;
  cluster: string;
  subclusters: string[];
  code: string;
  occupation: string;
}

interface GroupedCluster {
  name: string;
  subclusters: {
    name: string;
    items: CareerItem[];
  }[];
}

export const CareerMappingTabContent: React.FC = () => {
  // States
  const [careers, setCareers] = useState<CareerItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    isDanger?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isDanger: false
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, isDanger = false) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        onConfirm();
        closeConfirm();
      },
      isDanger
    });
  };

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Accordion Toggles
  const [expandedClusters, setExpandedClusters] = useState<string[]>([]);
  const [expandedSubclusters, setExpandedSubclusters] = useState<string[]>([]);

  // Manual Add Form State
  const [formData, setFormData] = useState({
    cluster: '',
    subclusters: '',
    code: '',
    occupation: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch Career Repository on mount
  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    setIsLoading(true);
    setUploadError(null);
    try {
      const q = collection(db, 'careers');
      const querySnapshot = await getDocs(q);
      const loadedCareers: CareerItem[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loadedCareers.push({
          id: doc.id,
          cluster: data.cluster || '',
          subclusters: Array.isArray(data.subclusters) ? data.subclusters : [],
          code: data.code || '',
          occupation: data.occupation || ''
        });
      });
      setCareers(loadedCareers);
    } catch (err: any) {
      console.error("Error fetching careers:", err);
      setUploadError(`Failed to load career repository: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Drag and Drop State
  const [isDragActive, setIsDragActive] = useState(false);

  // Parse CSV function
  const processCSVFile = (file: File) => {
    setUploadProgress('Processing CSV file...');
    setUploadError(null);
    setSuccessMessage(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data as any[];
        if (data.length === 0) {
          setUploadError("The uploaded CSV file contains no records.");
          setUploadProgress(null);
          return;
        }

        // Loose key matching to be extremely resilient
        const sampleRow = data[0];
        const findKey = (candidates: string[]) => {
          return Object.keys(sampleRow).find(key => 
            candidates.some(cand => key.toLowerCase().trim() === cand.toLowerCase())
          ) || null;
        };

        const clusterKey = findKey(["cluster", "career cluster", "highest cluster", "major cluster"]);
        const subclusterKey = findKey(["subcluster", "subclusters", "subcluster(s)", "sub-cluster", "sub-cluster(s)", "pathway"]);
        const codeKey = findKey(["code", "career code", "occupation code", "onet code", "onet-code"]);
        const occupationKey = findKey(["occupation", "occupations", "job", "job title", "title", "career"]);

        if (!clusterKey || !occupationKey) {
          setUploadError("CSV must contain at least a 'Cluster' and an 'Occupation' column.");
          setUploadProgress(null);
          return;
        }

        setUploadProgress(`Uploading ${data.length} records to database...`);
        
        try {
          let count = 0;
          let batch = writeBatch(db);

          for (const row of data) {
            const rawCluster = row[clusterKey] || '';
            const rawSubcluster = subclusterKey ? row[subclusterKey] : '';
            const rawCode = codeKey ? row[codeKey] : '';
            const rawOccupation = row[occupationKey] || '';

            if (!rawCluster.trim() && !rawOccupation.trim()) continue;

            // Split subclusters by semicolon
            const subclustersArray = rawSubcluster
              ? rawSubcluster.split(';').map((s: string) => s.trim()).filter(Boolean)
              : [];

            const careerRef = doc(collection(db, 'careers'));
            const newItem = {
              cluster: rawCluster.trim(),
              subclusters: subclustersArray,
              code: rawCode.trim(),
              occupation: rawOccupation.trim()
            };

            batch.set(careerRef, newItem);
            count++;

            // Commit in chunks of 500
            if (count === 500) {
              await batch.commit();
              batch = writeBatch(db);
              count = 0;
            }
          }

          if (count > 0) {
            await batch.commit();
          }

          setUploadProgress(null);
          setSuccessMessage(`Successfully uploaded ${data.length} occupation profiles!`);
          fetchCareers();
        } catch (err: any) {
          console.error("Batch write failed:", err);
          setUploadError(`Failed to save occupations to Firestore: ${err.message}`);
          setUploadProgress(null);
        }
      },
      error: (err) => {
        setUploadError(`CSV Parsing failed: ${err.message}`);
        setUploadProgress(null);
      }
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processCSVFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processCSVFile(e.dataTransfer.files[0]);
    }
  };

  // Add Individual Record
  const handleAddIndividual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cluster.trim() || !formData.occupation.trim()) {
      setUploadError("Cluster and Occupation fields are required.");
      return;
    }

    setLoadingAction(true);
    setUploadError(null);
    setSuccessMessage(null);

    try {
      const subclustersArray = formData.subclusters
        ? formData.subclusters.split(';').map(s => s.trim()).filter(Boolean)
        : [];

      const payload = {
        cluster: formData.cluster.trim(),
        subclusters: subclustersArray,
        code: formData.code.trim(),
        occupation: formData.occupation.trim()
      };

      const docRef = await addDoc(collection(db, 'careers'), payload);
      
      setCareers(prev => [
        { id: docRef.id, ...payload },
        ...prev
      ]);

      setSuccessMessage(`Successfully added occupation "${formData.occupation}"`);
      setFormData({ cluster: '', subclusters: '', code: '', occupation: '' });
    } catch (err: any) {
      setUploadError(`Failed to save record: ${err.message}`);
    } finally {
      setLoadingAction(false);
    }
  };

  // Delete Individual Record
  const handleDeleteIndividual = (id: string, name: string) => {
    showConfirm(
      "Delete Occupation",
      `Are you sure you want to permanently delete "${name}" from the repository?`,
      async () => {
        setLoadingAction(true);
        setUploadError(null);
        setSuccessMessage(null);

        try {
          await deleteDoc(doc(db, 'careers', id));
          setCareers(prev => prev.filter(item => item.id !== id));
          setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setSuccessMessage(`Successfully deleted occupation.`);
        } catch (err: any) {
          setUploadError(`Deletion failed: ${err.message}`);
        } finally {
          setLoadingAction(false);
        }
      },
      true
    );
  };

  // Bulk Delete Selected
  const handleDeleteSelected = () => {
    const listToDelete = Array.from(selectedIds) as string[];
    if (listToDelete.length === 0) return;

    showConfirm(
      "Delete Selected Occupations",
      `Are you sure you want to delete all ${listToDelete.length} selected occupations?`,
      async () => {
        setLoadingAction(true);
        setUploadError(null);
        setSuccessMessage(null);

        try {
          let count = 0;
          let batch = writeBatch(db);

          for (const id of listToDelete) {
            batch.delete(doc(db, 'careers', id));
            count++;

            if (count === 500) {
              await batch.commit();
              batch = writeBatch(db);
              count = 0;
            }
          }

          if (count > 0) {
            await batch.commit();
          }

          setCareers(prev => prev.filter(item => !selectedIds.has(item.id)));
          setSelectedIds(new Set());
          setSuccessMessage(`Successfully deleted ${listToDelete.length} selected records.`);
        } catch (err: any) {
          setUploadError(`Failed to complete bulk deletion: ${err.message}`);
        } finally {
          setLoadingAction(false);
        }
      },
      true
    );
  };

  // Clear entire Repository
  const handleClearAll = () => {
    if (careers.length === 0) {
      setUploadError("The repository is already empty.");
      return;
    }

    showConfirm(
      "Clear Career Repository",
      "CRITICAL WARNING: This will permanently delete ALL career occupations in your repository! This action is irreversible. Are you absolutely sure?",
      async () => {
        setLoadingAction(true);
        setUploadError(null);
        setSuccessMessage(null);

        try {
          let count = 0;
          let batch = writeBatch(db);

          for (const item of careers) {
            batch.delete(doc(db, 'careers', item.id));
            count++;

            if (count === 500) {
              await batch.commit();
              batch = writeBatch(db);
              count = 0;
            }
          }

          if (count > 0) {
            await batch.commit();
          }

          setCareers([]);
          setSelectedIds(new Set());
          setSuccessMessage(`Entire career repository has been cleared.`);
        } catch (err: any) {
          setUploadError(`Failed to clear repository: ${err.message}`);
        } finally {
          setLoadingAction(false);
        }
      },
      true
    );
  };

  // Download Sample Template CSV
  const handleDownloadTemplate = () => {
    const headers = ["Cluster", "Subcluster(s)", "Code", "Occupation"];
    const rows = [
      ["Agriculture, Food & Natural Resources", "Agribusiness Systems; Animal Systems", "11-9013.00", "Farmers, Ranchers, and Other Agricultural Managers"],
      ["Health Science", "Therapeutic Services; Diagnostics Services", "29-1123.00", "Physical Therapists"],
      ["Information Technology", "Web and Digital Communications; Programming and Software Development", "15-1254.00", "Web Developers"],
      ["Business Management & Administration", "General Management; Business Information Management", "11-1021.00", "General and Operations Managers"]
    ];
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(e => e.map(val => `"${val.replace(/"/g, '""')}"`).join(","))].join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "career_repository_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter Careers based on search query
  const filteredCareers = useMemo(() => {
    if (!searchQuery.trim()) return careers;
    const query = searchQuery.toLowerCase().trim();
    return careers.filter(item => 
      item.occupation.toLowerCase().includes(query) ||
      item.cluster.toLowerCase().includes(query) ||
      item.code.toLowerCase().includes(query) ||
      item.subclusters.some(sub => sub.toLowerCase().includes(query))
    );
  }, [careers, searchQuery]);

  // Group Filtered Careers into Hierarchical Accordion Structure
  const groupedData = useMemo(() => {
    const clusterMap: { [clusterName: string]: { [subclusterName: string]: CareerItem[] } } = {};

    filteredCareers.forEach((item) => {
      const cluster = item.cluster ? item.cluster.trim() : "Unassigned Clusters";
      if (!clusterMap[cluster]) {
        clusterMap[cluster] = {};
      }

      const subclusters = item.subclusters && item.subclusters.length > 0 
        ? item.subclusters 
        : ["General"];

      subclusters.forEach((sub) => {
        const subTrimmed = sub ? sub.trim() : "General";
        if (!clusterMap[cluster][subTrimmed]) {
          clusterMap[cluster][subTrimmed] = [];
        }
        // Avoid duplicate representations within same subcluster bucket
        if (!clusterMap[cluster][subTrimmed].some(c => c.id === item.id)) {
          clusterMap[cluster][subTrimmed].push(item);
        }
      });
    });

    // Build sorted array list
    const sortedResult: GroupedCluster[] = Object.keys(clusterMap)
      .sort()
      .map((clusterName) => {
        const subList = Object.keys(clusterMap[clusterName])
          .sort()
          .map((subName) => ({
            name: subName,
            items: clusterMap[clusterName][subName].sort((a, b) => (a.occupation || '').localeCompare(b.occupation || ''))
          }));
        return {
          name: clusterName,
          subclusters: subList
        };
      });

    return sortedResult;
  }, [filteredCareers]);

  // Bulk Selection Control
  const allFilteredIds = useMemo(() => {
    return filteredCareers.map(c => c.id);
  }, [filteredCareers]);

  const isAllSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.has(id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allFilteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handleCheckboxChange = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Toggle helpers
  const toggleCluster = (name: string) => {
    setExpandedClusters(prev => 
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const toggleSubcluster = (key: string) => {
    setExpandedSubclusters(prev => 
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="space-y-6">
      
      {/* Overview Card */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-md space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-xl font-black text-[#011d3c] tracking-tight uppercase flex items-center gap-2">
              <Database className="w-6 h-6 text-[#0094c8]" />
              <span>Career Information Repository</span>
            </h2>
            <div className="text-[10px] font-extrabold text-slate-400 tracking-widest uppercase">
              Manage centralized career clusters, pathways, and O*NET occupations.
            </div>
          </div>
          
          <button 
            onClick={fetchCareers}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 disabled:opacity-50 text-xs font-bold rounded-xl transition cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Control Actions Bento Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* CSV Import Panel */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-md flex flex-col justify-between space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-black text-[#011d3c] uppercase flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-[#0094c8]" />
              <span>Bulk CSV Import</span>
            </h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Upload a single CSV file with exactly four columns: <span className="font-semibold text-slate-700">Cluster, Subcluster(s), Code, Occupation</span>. Subclusters must be separated by semicolons (<code className="bg-slate-100 px-1 rounded font-mono font-semibold">;</code>) to list occupations across multiple pathways.
            </p>
          </div>

          {/* Drag & Drop File Zone */}
          <div 
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all ${
              isDragActive 
                ? 'border-[#0094c8] bg-sky-50/50 scale-[0.99]' 
                : 'border-slate-200 hover:border-[#0094c8]/50 hover:bg-slate-50/50'
            }`}
          >
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileUpload} 
              accept=".csv" 
              className="hidden" 
            />
            <Upload className="w-8 h-8 text-slate-400 mb-2" />
            <div className="text-xs font-bold text-slate-700">
              Drag & drop your Career CSV here or <span className="text-[#0094c8] hover:underline">browse files</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Supports standard CSV spreadsheets</div>
          </div>

          {/* Action Tools */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={handleDownloadTemplate}
              className="px-3.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition cursor-pointer"
            >
              Get Template CSV
            </button>
            <button
              onClick={handleClearAll}
              disabled={loadingAction || careers.length === 0}
              className="px-3.5 py-1.5 border border-red-200 hover:bg-red-50 text-red-600 font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
            >
              Clear Repository En Masse
            </button>
          </div>
        </div>

        {/* Individual Add Record Panel */}
        <form onSubmit={handleAddIndividual} className="bg-white rounded-3xl border border-slate-200 p-6 shadow-md flex flex-col justify-between space-y-4">
          <div className="space-y-1">
            <h3 className="text-sm font-black text-[#011d3c] uppercase flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#0094c8]" />
              <span>Add Individual Career Profile</span>
            </h3>
            <p className="text-xs text-slate-400">Quickly insert a single occupation record into your system directory.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase">Career Cluster <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                placeholder="e.g. Health Science"
                value={formData.cluster}
                onChange={e => setFormData(p => ({ ...p, cluster: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 focus:border-[#0094c8] focus:bg-white focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs outline-none transition"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase">Occupation Code (O*NET)</label>
              <input
                type="text"
                placeholder="e.g. 29-1123.00"
                value={formData.code}
                onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 focus:border-[#0094c8] focus:bg-white focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs outline-none transition"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase">Subcluster(s) <span className="text-slate-400 font-normal">(Separated by semicolons)</span></label>
              <input
                type="text"
                placeholder="e.g. Therapeutic Services; Diagnostics Services"
                value={formData.subclusters}
                onChange={e => setFormData(p => ({ ...p, subclusters: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 focus:border-[#0094c8] focus:bg-white focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs outline-none transition"
              />
            </div>

            <div className="sm:col-span-2 space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase">Occupation Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                placeholder="e.g. Physical Therapists"
                value={formData.occupation}
                onChange={e => setFormData(p => ({ ...p, occupation: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 focus:border-[#0094c8] focus:bg-white focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs outline-none transition"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={loadingAction || !formData.cluster.trim() || !formData.occupation.trim()}
              className="flex items-center gap-1 px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] disabled:opacity-50 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl shadow-sm transition cursor-pointer"
            >
              {loadingAction && <Loader2 className="w-3 h-3 animate-spin" />}
              <span>Add Career Record</span>
            </button>
          </div>
        </form>

      </div>

      {/* Status Messages */}
      {uploadProgress && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold rounded-2xl flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          <span>{uploadProgress}</span>
        </div>
      )}

      {uploadError && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-2xl flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span>{uploadError}</span>
          </div>
          <button onClick={() => setUploadError(null)} className="hover:bg-red-100 p-1 rounded-lg transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold rounded-2xl flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="hover:bg-emerald-100 p-1 rounded-lg transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Search & Bulk Select Toolkit */}
      <div className="bg-white rounded-3xl border border-slate-200 p-4 shadow-md space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          
          {/* Left: Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search careers, clusters, codes..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 focus:border-[#0094c8] focus:bg-white focus:ring-1 focus:ring-[#0094c8] rounded-xl text-xs outline-none transition"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 hover:bg-slate-200 p-0.5 rounded-full transition"
              >
                <X className="w-3 h-3 text-slate-400" />
              </button>
            )}
          </div>

          {/* Right: Selection Counter & Multi-Delete */}
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <div className="text-[11px] font-bold text-slate-500">
              {careers.length === 0 ? (
                <span>No career items loaded</span>
              ) : (
                <span>Showing {filteredCareers.length} of {careers.length} careers</span>
              )}
            </div>

            {selectedIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition shadow-sm cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Selected ({selectedIds.size})</span>
              </button>
            )}
          </div>

        </div>

        {/* Global Select All Box (Only when careers are present) */}
        {filteredCareers.length > 0 && (
          <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-xl border border-slate-150">
            <input
              type="checkbox"
              id="select-all-filtered"
              checked={isAllSelected}
              onChange={handleSelectAll}
              className="rounded text-[#0094c8] focus:ring-[#0094c8] border-slate-300 w-4 h-4 cursor-pointer"
            />
            <label htmlFor="select-all-filtered" className="text-xs font-extrabold text-slate-600 cursor-pointer select-none">
              {isAllSelected ? 'Deselect All matching careers' : 'Select All matching careers'}
            </label>
          </div>
        )}
      </div>

      {/* Grouped Accordion Viewer */}
      {isLoading ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-md flex flex-col items-center justify-center space-y-3">
          <Loader2 className="w-8 h-8 text-[#0094c8] animate-spin" />
          <p className="text-xs text-slate-500 font-bold">Querying system career catalogs...</p>
        </div>
      ) : groupedData.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-md space-y-3">
          <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto border border-slate-100">
            <Database className="w-6 h-6 text-slate-300" />
          </div>
          <h4 className="text-sm font-black text-[#011d3c] uppercase">No Careers Listed</h4>
          <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            {isSearching 
              ? "Your current query didn't yield any matches in our database clusters. Try refining your keywords."
              : "The centralized career mapping repository is currently empty. Complete a bulk import or use the form above to initialize occupational tracks."
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          
          {groupedData.map((cluster) => {
            const isClusterExpanded = expandedClusters.includes(cluster.name) || isSearching;
            
            // Total items in this cluster
            const clusterItemsCount = cluster.subclusters.reduce((sum, s) => sum + s.items.length, 0);

            return (
              <div 
                key={cluster.name} 
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
              >
                
                {/* Cluster Accordion Header */}
                <div 
                  onClick={() => toggleCluster(cluster.name)}
                  className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100/70 border-b border-slate-150 transition cursor-pointer select-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-slate-400">
                      {isClusterExpanded ? <ChevronDown className="w-5 h-5 text-slate-600" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                    <div>
                      <h4 className="text-xs md:text-sm font-black text-[#011d3c] uppercase tracking-tight">
                        {cluster.name}
                      </h4>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {cluster.subclusters.length} Pathways • {clusterItemsCount} Occupations
                      </div>
                    </div>
                  </div>

                  <Sparkles className="w-4 h-4 text-[#0094c8]/40" />
                </div>

                {/* Subclusters Content area */}
                {isClusterExpanded && (
                  <div className="p-4 space-y-4 bg-white">
                    {cluster.subclusters.map((sub) => {
                      const subclusterKey = `${cluster.name}|${sub.name}`;
                      const isSubclusterExpanded = expandedSubclusters.includes(subclusterKey) || isSearching;

                      return (
                        <div 
                          key={sub.name}
                          className="border border-slate-150 rounded-xl overflow-hidden"
                        >
                          {/* Subcluster Accordion Header */}
                          <div
                            onClick={() => toggleSubcluster(subclusterKey)}
                            className="flex items-center justify-between px-4 py-2.5 bg-slate-50/50 hover:bg-slate-50 border-b border-slate-150 transition cursor-pointer select-none"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-slate-400">
                                {isSubclusterExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4" />}
                              </div>
                              <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">
                                {sub.name}
                              </span>
                              <span className="bg-slate-200 text-slate-700 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full">
                                {sub.items.length}
                              </span>
                            </div>
                          </div>

                          {/* Occupations List */}
                          {isSubclusterExpanded && (
                            <div className="divide-y divide-slate-100">
                              {sub.items.map((item) => {
                                const isChecked = selectedIds.has(item.id);

                                return (
                                  <div 
                                    key={item.id}
                                    className={`flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition ${
                                      isChecked ? 'bg-sky-50/20' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleCheckboxChange(item.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded text-[#0094c8] focus:ring-[#0094c8] border-slate-300 w-3.5 h-3.5 cursor-pointer"
                                      />
                                      
                                      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                                        {item.code && (
                                          <span className="font-mono text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded tracking-tight">
                                            {item.code}
                                          </span>
                                        )}
                                        <span className="text-xs font-bold text-[#011d3c]">
                                          {item.occupation}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    <button
                                      onClick={() => handleDeleteIndividual(item.id, item.occupation)}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                      title="Delete occupation"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                        </div>
                      );
                    })}
                  </div>
                )}

              </div>
            );
          })}

        </div>
      )}

      {/* Elegant Custom Confirmation Modal Overlay */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 max-w-md w-full shadow-2xl space-y-4 transform transition-all duration-300 scale-100">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-2xl ${confirmModal.isDanger ? 'bg-red-50 text-red-600' : 'bg-sky-50 text-[#0094c8]'}`}>
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-base font-black text-[#011d3c] uppercase tracking-tight">
                {confirmModal.title}
              </h3>
            </div>
            
            <p className="text-xs text-slate-500 leading-relaxed">
              {confirmModal.message}
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeConfirm}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmModal.onConfirm}
                className={`px-4 py-2 text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl shadow-sm transition cursor-pointer ${
                  confirmModal.isDanger 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-[#0094c8] hover:bg-[#007ba6]'
                }`}
              >
                Confirm Action
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
